import { useMemo, useState } from "react";

import { IPermissionCatalogItem } from "../../../@types/admin/AdminAccess";
import {
  ChecklistContainer,
  ChecklistEmpty,
  ChecklistGrid,
  ChecklistGroup,
  ChecklistGroupCount,
  ChecklistGroupHeader,
  ChecklistGroupName,
  ChecklistInheritedTag,
  ChecklistItem,
  ChecklistItemCode,
  ChecklistItemLabel,
  ChecklistItemText,
  ChecklistSearch,
  ChecklistSummary,
  ChecklistToolbar,
} from "./Access.styles";
import {
  filterPermissionGroups,
  groupPermissions,
  isRedundantDisplayName,
} from "./permissionGroups";

/** Below this the flat list is readable and a search box is just chrome. */
const SEARCH_THRESHOLD = 10;

interface IPermissionChecklistProps {
  catalog: IPermissionCatalogItem[];
  granted: Set<string>;
  /**
   * Permissions the user's role already grants. Shown checked and locked —
   * they can only be changed on the Role Permissions page, so toggling them
   * here would silently do nothing.
   */
  inherited?: Set<string>;
  disabled?: boolean;
  onToggle: (permissionCode: string, grant: boolean) => void;
}

/**
 * Checkbox list over the permission catalog — shared by the Role Permissions
 * and User Access manage modals (EN-5129). Checked = granted; toggling calls
 * onToggle with the desired state.
 *
 * Grouped by the sidebar section each permission's page belongs to, because
 * the catalog grows by one entry per migrated Retool page and a flat column
 * stopped being scannable somewhere around a dozen. The grouping is derived
 * from `adminMenuGroups`, so it follows the sidebar without being maintained
 * separately — see `permissionGroups.ts`.
 */
export function PermissionChecklist({
  catalog,
  granted,
  inherited,
  disabled = false,
  onToggle,
}: IPermissionChecklistProps) {
  const isEffectivelyGranted = (code: string) =>
    granted.has(code) || (inherited?.has(code) ?? false);
  const [searchTerm, setSearchTerm] = useState("");

  const groups = useMemo(() => groupPermissions(catalog), [catalog]);
  const visibleGroups = useMemo(
    () => filterPermissionGroups(groups, searchTerm),
    [groups, searchTerm]
  );

  const grantedCount = catalog.filter((permission) =>
    isEffectivelyGranted(permission.code)
  ).length;
  const showSearch = catalog.length > SEARCH_THRESHOLD;

  return (
    <>
      {showSearch && (
        <ChecklistToolbar>
          <ChecklistSearch
            type="text"
            aria-label="Search permissions"
            placeholder="Search permissions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <ChecklistSummary>
            {grantedCount} of {catalog.length} granted
          </ChecklistSummary>
        </ChecklistToolbar>
      )}

      <ChecklistContainer>
        {visibleGroups.length === 0 ? (
          <ChecklistEmpty>
            No permission matches &ldquo;{searchTerm.trim()}&rdquo;.
          </ChecklistEmpty>
        ) : (
          visibleGroups.map((group) => {
            const groupGranted = group.permissions.filter((permission) =>
              isEffectivelyGranted(permission.code)
            ).length;

            return (
              <ChecklistGroup key={group.name}>
                <ChecklistGroupHeader>
                  <ChecklistGroupName>{group.name}</ChecklistGroupName>
                  <ChecklistGroupCount>
                    {groupGranted} of {group.permissions.length}
                  </ChecklistGroupCount>
                </ChecklistGroupHeader>

                <ChecklistGrid>
                  {group.permissions.map((permission) => {
                    const isInherited =
                      inherited?.has(permission.code) ?? false;
                    const isChecked =
                      granted.has(permission.code) || isInherited;
                    return (
                      <ChecklistItem
                        key={permission.code}
                        disabled={disabled}
                        locked={isInherited}
                        title={
                          isInherited
                            ? `${permission.code} — granted by the role`
                            : `${permission.code} — ${permission.displayName}`
                        }
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={disabled || isInherited}
                          onChange={() => onToggle(permission.code, !isChecked)}
                        />
                        <ChecklistItemText>
                          <ChecklistItemCode>
                            {permission.code}
                            {isInherited && (
                              <ChecklistInheritedTag>
                                via role
                              </ChecklistInheritedTag>
                            )}
                          </ChecklistItemCode>
                          {/* Only where the wording adds something the code
                              and the group heading don't already say. */}
                          {!isRedundantDisplayName(permission) && (
                            <ChecklistItemLabel>
                              {permission.displayName}
                            </ChecklistItemLabel>
                          )}
                        </ChecklistItemText>
                      </ChecklistItem>
                    );
                  })}
                </ChecklistGrid>
              </ChecklistGroup>
            );
          })
        )}
      </ChecklistContainer>
    </>
  );
}
